import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { trackEvent } from '../../utils/analytics_utils';

interface TutorialInfo {
  key: string;
  startTutorial: () => void;
}

interface GlobalTutorialContextType {
  availableTutorials: TutorialInfo[];
  registerTutorial: (tutorial: TutorialInfo) => void;
  unregisterTutorial: (key: string) => void;
  startTutorial: (key?: string) => void;
  hasTutorials: boolean;
}

const GlobalTutorialContext = createContext<GlobalTutorialContextType>({
  availableTutorials: [],
  registerTutorial: () => {},
  unregisterTutorial: () => {},
  startTutorial: () => {},
  hasTutorials: false,
});

export const useGlobalTutorial = () => useContext(GlobalTutorialContext);

export const GlobalTutorialProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [availableTutorials, setAvailableTutorials] = useState<TutorialInfo[]>([]);

  const registerTutorial = useCallback((tutorial: TutorialInfo) => {
    setAvailableTutorials(prev => {
      const idx = prev.findIndex(t => t.key === tutorial.key);
      if (idx !== -1) {
        // Already registered with the same key — return same array to avoid re-render
        if (prev[idx].startTutorial === tutorial.startTutorial) return prev;
        const next = [...prev];
        next[idx] = tutorial;
        return next;
      }
      return [...prev, tutorial];
    });
  }, []);

  const unregisterTutorial = useCallback((key: string) => {
    setAvailableTutorials(prev => {
      const next = prev.filter(t => t.key !== key);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const startTutorial = useCallback((key?: string) => {
    setAvailableTutorials(current => {
      const target = key
        ? current.find(t => t.key === key)
        : current[0];

      if (target) {
        target.startTutorial();
        trackEvent({
          event: 'tutorial_restarted',
          tutorial_id: target.key,
          source: 'help_menu',
        });
      }
      return current; // no state change
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      availableTutorials,
      registerTutorial,
      unregisterTutorial,
      startTutorial,
      hasTutorials: availableTutorials.length > 0,
    }),
    [availableTutorials, registerTutorial, unregisterTutorial, startTutorial],
  );

  return (
    <GlobalTutorialContext.Provider value={contextValue}>
      {children}
    </GlobalTutorialContext.Provider>
  );
};
